import React, { memo, useState } from 'react';
import ListHeroPhoto from '../ListHeroPhoto';
import './CollectionHero.css';

/**
 * CollectionHero component displays a hero image for a collection.
 * - If customHeroUrl is provided, shows that single image
 * - Otherwise, creates a collage from the first 1-4 lists' hero photos
 * - Layout adapts: 1 photo = full, 2 = side by side, 3 = row, 4 = 2x2 grid
 */
const CollectionHero = memo(function CollectionHero({
  lists = [],
  customHeroUrl = null,
  altText = 'Collection',
}) {
  // Tracks a custom hero URL that failed to load so we can fall back to the
  // collage/placeholder. Comparing against the current URL means a new URL
  // gets a fresh attempt without needing an effect to reset state.
  const [failedHeroUrl, setFailedHeroUrl] = useState(null);

  // If custom hero is provided and loadable, use it directly
  if (customHeroUrl && customHeroUrl !== failedHeroUrl) {
    return (
      <div className="collection-hero collection-hero--single">
        <img
          src={customHeroUrl}
          alt={altText}
          className="collection-hero__image"
          onError={() => setFailedHeroUrl(customHeroUrl)}
        />
      </div>
    );
  }

  // Get up to 4 lists with hero photos
  const listsWithPhotos = lists
    .filter(list => {
      // Check for custom photo or items with photos
      if (list.customPhoto) return true;
      const heroItem = (list.items || []).find(item => item.photoUrl);
      return !!heroItem;
    })
    .slice(0, 4);

  // If no lists with photos, show placeholder
  if (listsWithPhotos.length === 0) {
    return (
      <div className="collection-hero collection-hero--empty">
        <div className="collection-hero__placeholder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
      </div>
    );
  }

  // Determine layout class based on number of photos
  const layoutClass = `collection-hero--${listsWithPhotos.length}`;

  return (
    <div className={`collection-hero ${layoutClass}`}>
      {listsWithPhotos.map((list, index) => {
        const heroItem = (list.items || []).find(item => item.photoUrl);

        return (
          <div key={list.id || index} className="collection-hero__cell">
            <ListHeroPhoto
              placeId={heroItem?.placeId}
              storedPhotoUrl={heroItem?.photoUrl}
              customPhoto={list.customPhoto}
              altText={list.title || list.name || 'List'}
            />
          </div>
        );
      })}
    </div>
  );
});

export default CollectionHero;
